/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useCallback } from 'react';
import { useResource } from '@atlassian/react-resource-router';
// eslint-disable-next-line jira/import-whitelist
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
// eslint-disable-next-line jira/import-whitelist
import { userPreferenceAPI } from '@jira/platform__services-user-preferences/src/utils/index.tsx';
import {
	GIC_MINI_MODAL_DISCOVERABILITY_PREFERENCE_KEY,
	GIC_PERSISTENT_STATE_PREFERENCE_KEY,
} from './gicMiniModalDiscoverabilityPreferenceKey.constants.tsx';
import type {
	PersistentIssueCreateModalView,
	PersistentIssueCreateModalViewResource,
} from './PersistentIssueCreateModalView.types.tsx';
import { persistentIssueCreateModalViewResource } from './persistentIssueCreateModalViewResource.tsx';

export const usePersistentIssueCreateModalViewResource = (): {
	data: PersistentIssueCreateModalViewResource | null;
	updateView: (view: PersistentIssueCreateModalView) => Promise<void>;
	updateIsDiscoPushComplete: (isDiscoPushComplete?: boolean) => Promise<void>;
} => {
	const { data, update } = useResource(persistentIssueCreateModalViewResource);

	const updateView = useCallback(
		async (view: PersistentIssueCreateModalView): Promise<void> => {
			try {
				await userPreferenceAPI.set(GIC_PERSISTENT_STATE_PREFERENCE_KEY, view);
				update((state) => ({
					...state,
					view,
				}));
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'persistentIssueCreateModalViewState',
						packageName: 'jiraRouterResourcesPersistentIssueCreateModalView',
						teamName: 'gryffindor',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
		[update],
	);

	const updateIsDiscoPushComplete = useCallback(
		async (isDiscoPushComplete: boolean = true): Promise<void> => {
			try {
				await userPreferenceAPI.set(
					GIC_MINI_MODAL_DISCOVERABILITY_PREFERENCE_KEY,
					isDiscoPushComplete,
				);
				update((state) => ({
					...state,
					isDiscoPushComplete,
				}));
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				fireErrorAnalytics({
					error,
					meta: {
						id: 'miniModalDiscoverabilityPushState',
						packageName: 'jiraRouterResourcesPersistentIssueCreateModalView',
						teamName: 'gryffindor',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
			}
		},
		[update],
	);

	return {
		data,
		updateView,
		updateIsDiscoPushComplete,
	};
};
